
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import StarTabBar from '@/components/star/StarTabBar.vue'
import FactorSelector from '@/components/star/FactorSelector.vue'
import StockRangeSelector from '@/components/star/StockRangeSelector.vue'
import TradingRules from '@/components/star/TradingRules.vue'
import PositionControl from '@/components/star/PositionControl.vue'
import BacktestPeriod from '@/components/star/BacktestPeriod.vue'
import StarTipDialog from '@/components/star/StarTipDialog.vue'
import { useStarTheme, useStarRouter } from '@/utils/useStarTheme'
import starApi from '@/api/starApi'
import { useStarTip } from '@/utils/useStarTip'

const __default__ = defineComponent({
  name: 'StarGroup',
  components: { StarTabBar, FactorSelector, StockRangeSelector, TradingRules, PositionControl, BacktestPeriod, StarTipDialog },
  setup() {
    const route = useRoute()
    const store = useStore()
    const { isLight, colors } = useStarTheme()
    const { starPush } = useStarRouter()

    const isPassword = computed(() => route.query.isPassword === 'true')

    const comOptions = ref<any>(null)
    const factorOptions = ref<any>(null)
    const stockRangeOptions = ref<any>(null)
    const selectedFactors = ref<any[]>([])
    const watchwordData = ref<any>(null)  // 口令回显数据

    const factorHeaders = computed(() => ({
      token: route.query.token as string,
      appcode: 'com.rjhy.uranus',
      userName: store.state.userInfo?.userName
    }))

    const onFactorChange = (factors: any[]) => {
      selectedFactors.value = factors
    }

    const selectedRanges = ref<any[]>([])
    const onRangeChange = (ranges: any[]) => {
      selectedRanges.value = ranges
    }

    const fetchComOptions = async () => {
      try {
        const token = route.query.token as string
        const res = await starApi.getComOptions({
          headers: { token }
        })
        comOptions.value = res?.data || null
      } catch (e) { /* 静默失败 */ }
    }

    const fetchFactors = async () => {
      const token = route.query.token as string
      const userInfo = store.state.userInfo
      const userName = userInfo?.userName || (userInfo as any)?.username
      const headers = { token, appcode: 'com.rjhy.uranus', userName }
      const [factorRes, rangeRes] = await Promise.all([
        starApi.getFactors({ data: { from: 'quantstarfactor' }, headers }),
        starApi.getFactors({ data: { from: 'quantstarstockrange' }, headers })
      ])
      factorOptions.value = factorRes?.data || null
      stockRangeOptions.value = rangeRes?.data || null
    }

    const fetchWatchword = async () => {
      const word = route.query.watchWord as string
      if (!word) return
      try {
        const token = route.query.token as string
        const userName = store.state.userInfo?.userName
        const res = await starApi.redeemWatchword({
          data: { watchWord: word },
          headers: { token, userName }
        })
        if (res?.data?.status === 1) {
          watchwordData.value = res.data
        }
      } catch (e) { /* 静默失败 */ }
    }

    const goHome = () => starPush('/star')
    // 摘要数据
    const tradingData = ref<any>({ buyPriorityLabel: '', holdDaysLabel: '', indicatorsLabel: '', valid: false })
    const positionData = ref<any>({ initCapital: '100', maxSinglePos: '10', maxHoldCount: '10', maxDailyBuy: '2', valid: true })
    const backtestData = ref<any>({ periodLabel: '近3月', period: 'month3', valid: true })
    const showStockDialog = ref(false)
    const backtesting = ref(false)
    const showFactorTip = ref(false)
    const { tipDialog, showQrDialog, handleBacktestStatus } = useStarTip()

    const onTradingChange = (data: any) => { tradingData.value = data }
    const onPositionChange = (data: any) => { positionData.value = data }
    const onBacktestChange = (data: any) => { backtestData.value = data }

    const canBacktest = computed(() => {
      const tradingOk = !!tradingData.value.valid
      const positionOk = positionData.value.valid !== false
      const backtestOk = backtestData.value.valid !== false
      return tradingOk && positionOk && backtestOk
    })

    const sellSummary = computed(() => {
      const parts: string[] = []
      if (tradingData.value.holdDaysLabel) parts.push(`持股周期${tradingData.value.holdDaysLabel}卖出`)
      if (tradingData.value.indicatorsLabel) parts.push(tradingData.value.indicatorsLabel)
      const stops: string[] = []
      if (tradingData.value.stopProfit) stops.push(`止盈${tradingData.value.stopProfit}%`)
      if (tradingData.value.stopLoss) stops.push(`止损${tradingData.value.stopLoss}%`)
      if (tradingData.value.stockLoss) stops.push(`个股收益${tradingData.value.stockLoss}%`)
      if (tradingData.value.maxDrawdown) stops.push(`最高收益回落${tradingData.value.maxDrawdown}%`)
      if (stops.length) parts.push(stops.join('，'))
      if (tradingData.value.sellTimeLabel) parts.push(tradingData.value.sellTimeLabel)
      return parts.join('；')
    })

    const positionSummary = computed(() => {
      const d = positionData.value
      const parts: string[] = []
      if (d.initCapital) parts.push(`初始资金${d.initCapital}万`)
      if (d.maxSinglePos) parts.push(`交易${d.maxSinglePos}%`)
      if (d.maxHoldCount) parts.push(`账户最大持股数${d.maxHoldCount}只`)
      if (d.maxDailyBuy) parts.push(`单日最大买入数${d.maxDailyBuy}只`)
      return parts.join('；')
    })

    const handleBacktest = async () => {
      backtesting.value = true
      try {
        const token = route.query.token as string
        const userInfo = store.state.userInfo
        const userName = userInfo?.userName || (userInfo as any)?.username

        const td = tradingData.value
        const pd = positionData.value
        const bd = backtestData.value

        // factor：按 factorType 规则组装 options
        const factor = selectedFactors.value.map((f: any) => {
          const type = f.attrType
          let options: any[] = []
          let intersectionAndUnion = 0

          if (type === 1) {
            // 固定选项：只传 factorCode，options 为空
            options = []
          } else if (type === 2) {
            // 单选：options 传一个 optionCode 字符串
            options = f.selectedOptions?.length ? [f.selectedOptions[0].attrCode] : []
          } else if (type === 3 || type === 5) {
            // 多选并集 / 下拉多选：options 传 optionCode 字符串数组
            options = (f.selectedOptions || []).map((o: any) => o.attrCode)
          } else if (type === 4 || type === 6) {
            // 多选加交并 / 下拉多选加交并：options 传 optionCode 数组 + intersectionAndUnion
            options = (f.selectedOptions || []).map((o: any) => o.attrCode)
            intersectionAndUnion = f.unionMode === 'intersect' ? 0 : 1
          } else if (type === 7) {
            // 区间：options 传 object，gt/lte（当前多维选股王用 gt/lte）
            const r = f.selectedRange
            if (r) {
              const rangeObj: Record<string, number> = {}
              if (r.left !== '' && r.left != null) rangeObj.gte = Number(r.left)
              if (r.right !== '' && r.right != null) rangeObj.lte = Number(r.right)
              options = [rangeObj]
            }
          }

          return {
            factorCode: f.attrCode || f.factorCode || '',
            descript: (f.attrName || '').trim() || null,
            intersectionAndUnion,
            options,
          }
        })

        // stockRange：跟 factor 完全一样的 []SelectedItemV2 结构
        const stockRange = selectedRanges.value.map((f: any) => {
          const type = f.attrType
          let options: any[] = []
          let intersectionAndUnion = 0

          if (type === 1 || type == null) {
            options = []
          } else if (type === 2) {
            options = f.selectedOptions?.length ? [f.selectedOptions[0].attrCode] : []
          } else if (type === 3 || type === 5) {
            options = (f.selectedOptions || []).map((o: any) => o.attrCode)
          } else if (type === 4 || type === 6) {
            options = (f.selectedOptions || []).map((o: any) => o.attrCode)
            intersectionAndUnion = f.unionMode === 'intersect' ? 0 : 1
          } else if (type === 7) {
            const r = f.selectedRange
            if (r) {
              const rangeObj: Record<string, number> = {}
              if (r.left !== '' && r.left != null) rangeObj.gte = Number(r.left)
              if (r.right !== '' && r.right != null) rangeObj.lte = Number(r.right)
              options = [rangeObj]
            }
          }

          return {
            factorCode: f.attrCode || f.factorCode || '',
            descript: (f.attrName || '').trim() || null,
            intersectionAndUnion,
            options,
          }
        })

        // initialFund：万 → 元
        const initialFund = pd.initCapital ? Math.round(parseFloat(pd.initCapital) * 10000) : 1000000

        const body: Record<string, any> = {
          factor,
          buyTime: td.buyTime ?? 1,
          buyPriority: td.buyPriority || undefined,
          buyPriorityDirection: td.buyPriorityDirection || undefined,
          holdPeriod: td.holdPeriod ?? undefined,
          sellOptions: td.sellOptions?.length ? td.sellOptions : undefined,
          takeProfit: td.takeProfit ?? undefined,
          sellStopLoss: td.sellStopLoss ?? undefined,
          sellStockReturn: td.sellStockReturn ?? undefined,
          sellFallenBack: td.sellFallenBack ?? undefined,
          sellTime: td.sellTime ?? 1,
          initialFund,
          positionLimit: pd.maxSinglePos ? parseInt(pd.maxSinglePos) : undefined,
          maxHolds: pd.maxHoldCount ? parseInt(pd.maxHoldCount) : undefined,
          dayLimit: pd.maxDailyBuy ? parseInt(pd.maxDailyBuy) : undefined,
          period: bd.period || 'month3',
          periodStart: bd.periodStart ?? undefined,
          periodEnd: bd.periodEnd ?? undefined,
        }

        // 口令模式：忽略 factor/stockRange，传 watchWord
        if (isPassword.value) {
          body.watchWord = route.query.watchWord as string || undefined
        } else if (stockRange.length) {
          body.stockRange = stockRange
        }

        // 清理 undefined
        Object.keys(body).forEach(k => body[k] === undefined && delete body[k])

        const res = await starApi.doTestCom({
          data: body,
          headers: { token, appcode: 'com.rjhy.uranus', userName }
        })

        const taskID = res?.data?.taskID
        handleBacktestStatus(res?.data?.status, res?.data?.toast, () => {
          starPush('/star/group/result', taskID ? { taskID, isfrom: '1', ...(isPassword.value ? { isFactor: '1' } : {}) } : { isfrom: '1' })
        })
      } catch (e) {
        // 失败不跳转
      } finally {
        backtesting.value = false
      }
    }

    onMounted(() => {
      fetchComOptions()
      if (!isPassword.value) {
        fetchFactors()
      } else {
        fetchWatchword()
      }
    })

    return {
      activeTopTab: ref('home'),
      colors, isLight, isPassword,
      comOptions, factorOptions, stockRangeOptions,
      factorHeaders, selectedFactors, selectedRanges,
      watchwordData,
      tradingData, positionData, backtestData,
      sellSummary, positionSummary, canBacktest,
      showStockDialog, showFactorTip, backtesting,
      showQrDialog, tipDialog,
      onFactorChange, onRangeChange, onTradingChange, onPositionChange, onBacktestChange,
      goHome, handleBacktest
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "bef0c0ba": (_ctx.isLight ? '#f0f2f5' : '#2a2a2a'),
  "86c7761a": (_ctx.isLight ? '1px solid #dbdfe6' : 'none'),
  "228d022b": (_ctx.isLight ? '0 1px 4px rgba(0,0,0,0.06)' : 'none')
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__